import { useMemo } from 'react';

import {
  amountWithCurrencySymbol,
  CurrencyCode,
  DEFAULT_CURRENCIES,
  isCryptoCurrency,
} from '@/helpers/currency';
import { useGetCommonStore } from '@/store';

interface IExchangeAmount {
  amount: number;
  inputCurrency?: CurrencyCode;
  currencyType?: 'crypto' | 'fiat';
}

const EXCHANGE_AMOUNT_ROUNDING = 100;

export const useExchangeAmount = ({
  amount,
  currencyType,
  inputCurrency = CurrencyCode.USD,
}: IExchangeAmount) => {
  const useCommonStore = useGetCommonStore();

  const user = useCommonStore.use.user();
  const exchangeRates = useCommonStore.use.exchangeRates();

  const currencyFiatOrCrypto = useMemo(() => {
    if (currencyType) return currencyType;

    return isCryptoCurrency(DEFAULT_CURRENCIES, user?.currency as CurrencyCode)
      ? 'crypto'
      : 'fiat';
  }, [user, currencyType]);

  const exchangeAmount = useMemo(() => {
    if (!(exchangeRates && user)) return 0;

    const exchangeRate =
      exchangeRates[currencyFiatOrCrypto][inputCurrency][
        user.currency as CurrencyCode
      ]?.exchangeRate ?? 1;

    const calculatedMinDep = amount / exchangeRate;

    return (
      Math.floor(calculatedMinDep * EXCHANGE_AMOUNT_ROUNDING) /
      EXCHANGE_AMOUNT_ROUNDING
    );
  }, [exchangeRates, user, currencyFiatOrCrypto, amount, inputCurrency]);

  const exchangeAmountWithCurrency = useMemo(
    () => amountWithCurrencySymbol(user?.currency, exchangeAmount),
    [exchangeAmount, user?.currency]
  );

  return {
    exchangeAmount,
    exchangeAmountWithCurrency,
  };
};
